<template>
  <div>
    <app-template />
    <div class="my-payments">
      <h1>Payments</h1>
      <p class="muted" v-if="playerName">{{ playerName }}</p>

      <div v-if="loading" class="muted center">Loading…</div>

      <template v-else>
        <!-- Season fees (installments) -->
        <section v-if="seasonFees && seasonFees.mapped_or_account" class="card">
          <h2>{{ seasonFees.plan_name || "Season fees" }}</h2>
          <div class="summary" :class="{ cleared: seasonFees.ledger.cleared }">
            <template v-if="seasonFees.ledger.cleared">
              All paid — nothing owing. Thank you!
            </template>
            <template v-else>
              Outstanding: <strong>{{ money(seasonFees.ledger.outstanding) }}</strong>
              <span v-if="seasonFees.ledger.discount > 0" class="muted">
                (includes −{{ money(seasonFees.ledger.discount) }} voucher)
              </span>
            </template>
          </div>

          <table class="stages">
            <tbody>
              <tr v-for="s in seasonFees.ledger.installments" :key="s.id">
                <td>
                  <strong>{{ s.label }}</strong>
                  <span v-if="s.due_date" class="muted due">due {{ formatDate(s.due_date) }}</span>
                </td>
                <td class="amount">
                  <span v-if="s.outstanding_pence === 0" class="paid-pill">Paid</span>
                  <template v-else>{{ money(s.outstanding_pence) }}</template>
                </td>
                <td class="action">
                  <button v-if="s.outstanding_pence > 0" :disabled="busy" @click="payInstallment(s)">
                    Pay now
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Voucher -->
          <div v-if="seasonFees.voucher_scheme" class="voucher">
            <template v-if="currentClaim">
              <p class="muted">
                {{ seasonFees.voucher_scheme.label }}:
                <strong>{{ voucherStatusLabel(currentClaim) }}</strong>
              </p>
            </template>
            <template v-else>
              <button class="link-button" @click="showVoucher = !showVoucher">
                {{ showVoucher ? "Close" : `Claim ${seasonFees.voucher_scheme.label} (−${money(seasonFees.voucher_scheme.amount_pence)})` }}
              </button>
              <div v-if="showVoucher" class="voucher-form">
                <input v-model="voucherForm.school" placeholder="School" />
                <input v-model="voucherForm.year_level" placeholder="Year level (e.g. Year 7)" />
                <button :disabled="busy || !voucherForm.school" @click="claimVoucher">Submit claim</button>
              </div>
            </template>
          </div>
        </section>

        <!-- Fee plans to pay/subscribe (registration-fee clubs) -->
        <section v-if="needsPayment && plans.length" class="card">
          <h2>Membership fees</h2>
          <p class="muted">Choose your plan to pay securely with Stripe.</p>
          <div v-for="plan in plans" :key="plan.id" class="plan-row">
            <div>
              <strong>{{ plan.name }}</strong>
              <span class="muted">{{ formatPlanPrice(plan) }}</span>
            </div>
            <button :disabled="busy" @click="payPlan(plan)">Pay</button>
          </div>
        </section>

        <!-- Current status / history -->
        <section class="card">
          <h2>Your payments</h2>
          <div v-if="latest" class="status-line">
            Status:
            <span class="status-pill" :class="statusClass(latest.status)">{{ statusLabel(latest.status) }}</span>
          </div>
          <table v-if="history.length" class="history">
            <thead>
              <tr><th>Date</th><th>Plan / reason</th><th>Amount</th><th>Method</th><th>Status</th></tr>
            </thead>
            <tbody>
              <tr v-for="r in history" :key="r.id">
                <td>{{ formatDate(r.created_at) }}</td>
                <td>{{ r.plan_name || (r.payment_method === "waived" ? "Fee waiver" : "Payment") }}</td>
                <td>{{ r.amount_pence ? money(r.amount_pence) : "—" }}</td>
                <td>{{ methodLabel(r.payment_method) }}</td>
                <td><span class="status-pill" :class="statusClass(r.status)">{{ statusLabel(r.status) }}</span></td>
              </tr>
            </tbody>
          </table>
          <p v-else class="muted">No payment records yet.</p>

          <button v-if="hasStripeHistory" class="link-button" :disabled="busy" @click="openBillingPortal">
            Update card / manage payments
          </button>
        </section>

        <!-- Club fundraising — active campaigns targeted at your team(s) -->
        <section v-if="campaigns.length" class="card fundraising-card">
          <h2>Club fundraising</h2>
          <p class="muted">Support your club's active appeals — donations go straight to the club.</p>
          <div v-for="c in campaigns" :key="c.id" class="campaign-row">
            <div class="campaign-info">
              <strong>{{ c.title }}</strong>
              <p v-if="c.description" class="muted campaign-desc">{{ c.description }}</p>
              <p class="campaign-raised">
                <strong>{{ money(c.raised_pence) }}</strong>
                <span v-if="c.target_pence" class="muted"> of {{ money(c.target_pence) }}</span>
              </p>
              <div v-if="c.target_pence" class="progress-track">
                <div class="progress-fill" :style="{ width: progressPercent(c) + '%' }"></div>
              </div>
            </div>
            <a class="donate-button" :href="campaignUrl(c)" target="_blank" rel="noopener">Donate</a>
          </div>
        </section>

        <p v-if="error" class="error-text">{{ error }}</p>
        <p v-if="!seasonFees && !plans.length && !history.length && !campaigns.length" class="muted center">
          There's nothing to pay right now. The club will be in touch if fees are due.
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { StripeConnect } from "@/services/stripe-connect-service.js";
import {
  getPaymentsCurrency,
  isFeatureEnabled,
} from "@/config/tenant";
import AppTemplate from "../layouts/App";

export default {
  name: "MyPayments",
  components: { AppTemplate },
  data() {
    return {
      loading: true,
      busy: false,
      error: "",
      history: [],
      plans: [],
      seasonFees: null,
      campaigns: [],
      showVoucher: false,
      voucherForm: { school: "", year_level: "" },
    };
  },
  computed: {
    ...mapGetters(["getProfile"]),
    clubId() {
      return this.getProfile?.clubId;
    },
    userId() {
      return this.getProfile?.id;
    },
    playerName() {
      return this.getProfile?.name;
    },
    latest() {
      return this.history.length ? this.history[0] : null;
    },
    needsPayment() {
      const settled = ["active", "paid", "trialing", "waived"];
      return (
        (!this.seasonFees || !this.seasonFees.mapped_or_account) &&
        (!this.latest || !settled.includes(this.latest.status))
      );
    },
    hasStripeHistory() {
      return this.history.some((r) => r.payment_method === "stripe");
    },
    currentClaim() {
      const claims = this.seasonFees?.voucher_claims || [];
      const year = new Date().getFullYear();
      return claims.find((c) => c.calendar_year === year && c.status !== "rejected") || null;
    },
  },
  watch: {
    // The profile hydrates asynchronously (Vuex), so clubId/userId may be
    // undefined at created(). Load once they're available.
    getProfile: {
      immediate: true,
      handler(profile) {
        if (profile?.clubId && profile?.id && !this._loaded) {
          this._loaded = true;
          this.load();
        }
      },
    },
  },
  methods: {
    money(pence) {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: getPaymentsCurrency(),
      }).format((pence || 0) / 100);
    },
    formatDate(v) {
      if (!v) return "—";
      const d = new Date(v.replace(" ", "T"));
      return isNaN(d) ? v : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    },
    progressPercent(c) {
      if (!c.target_pence) return 0;
      return Math.min(100, Math.round((c.raised_pence / c.target_pence) * 100));
    },
    campaignUrl(c) {
      return `/support/${encodeURIComponent(this.clubId)}/${c.id}`;
    },
    formatPlanPrice(plan) {
      const amt = this.money(plan.amount_pence);
      if (plan.billing_interval === "month") return `${amt} / month`;
      if (plan.billing_interval === "year") return `${amt} / year`;
      return `${amt} one-off`;
    },
    methodLabel(m) {
      return { stripe: "Card", cash: "Cash", bank_transfer: "Bank transfer", other: "Other", waived: "—" }[m] || m;
    },
    statusLabel(s) {
      return {
        pending: "Not completed", active: "Active", paid: "Paid", trialing: "Trial",
        waived: "Waived", past_due: "Payment failed", canceled: "Cancelled",
        installments: "Season fees", unpaid: "Unpaid",
      }[s] || s || "—";
    },
    statusClass(s) {
      if (["active", "paid", "trialing"].includes(s)) return "good";
      if (["waived"].includes(s)) return "neutral";
      if (["past_due", "canceled", "unpaid"].includes(s)) return "bad";
      return "warn";
    },
    voucherStatusLabel(claim) {
      return {
        claimed: "submitted — awaiting club approval",
        approved: `approved — ${this.money(claim.amount_pence)} off your balance`,
        redeemed: `applied — ${this.money(claim.amount_pence)} off your balance`,
      }[claim.status] || claim.status;
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const clubId = this.clubId;
        const userId = this.userId;
        const [historyRes, seasonRes, plansRes, campaignsRes] = await Promise.all([
          StripeConnect.getMemberPaymentHistory(clubId, userId),
          StripeConnect.getMyInstallmentStatus(clubId, userId),
          isFeatureEnabled("registrationFees")
            ? StripeConnect.listFeePlans(clubId)
            : Promise.resolve({ data: { data: [] } }),
          StripeConnect.getMemberCampaigns(clubId, userId).catch(() => ({
            data: { data: [] },
          })),
        ]);
        this.history = historyRes.data.data || [];
        const s = seasonRes.data.data;
        // Normalise: the endpoint returns has_account; expose a friendly flag.
        this.seasonFees = s && s.has_account ? { ...s, mapped_or_account: true } : null;
        this.plans = (plansRes.data.data || []).filter((p) => p.active == 1 && p.plan_type !== "installments");
        this.campaigns = campaignsRes.data.data || [];
      } catch (e) {
        this.error = "Couldn't load your payments. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    async payInstallment(stage) {
      this.busy = true;
      this.error = "";
      try {
        const res = await StripeConnect.createInstallmentCheckout(
          this.clubId,
          this.userId,
          this.seasonFees.plan_id,
          stage.id,
        );
        window.location.href = res.data.data.url;
      } catch (e) {
        this.error = e?.response?.data?.error || "Couldn't start the payment.";
        this.busy = false;
      }
    },
    async payPlan(plan) {
      this.busy = true;
      this.error = "";
      try {
        const res = await StripeConnect.createFeeCheckout({
          clubId: this.clubId,
          feePlanId: plan.id,
          userId: this.userId,
        });
        window.location.href = res.data.data.url;
      } catch (e) {
        this.error = e?.response?.data?.error || "Couldn't start the payment.";
        this.busy = false;
      }
    },
    async claimVoucher() {
      this.busy = true;
      this.error = "";
      try {
        await StripeConnect.claimVoucher({
          clubId: this.clubId,
          userId: this.userId,
          feePlanId: this.seasonFees.plan_id,
          details: {
            child_name: this.playerName,
            school: this.voucherForm.school,
            year_level: this.voucherForm.year_level,
          },
        });
        this.showVoucher = false;
        await this.load();
      } catch (e) {
        this.error = e?.response?.data?.error || "Couldn't submit the claim.";
      } finally {
        this.busy = false;
      }
    },
    async openBillingPortal() {
      this.busy = true;
      this.error = "";
      try {
        const res = await StripeConnect.createBillingPortalLink(this.clubId, this.userId);
        window.location.href = res.data.data.url;
      } catch (e) {
        this.error = e?.response?.data?.error || "Couldn't open the billing portal.";
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.my-payments {
  max-width: 720px;
  margin: 0 auto;
  padding: 20px 16px;

  h1 { margin-bottom: 2px; }

  .muted {
    color: #6c757d;
    font-size: 0.9rem;
    &.center { text-align: center; padding: 24px 0; }
  }

  .card {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 18px;
    margin: 16px 0;

    h2 { font-size: 1.05rem; margin-bottom: 8px; }
  }

  .fundraising-card {
    .campaign-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 14px;
      padding: 12px 0;
      border-bottom: 1px solid #f1f3f5;

      &:last-child { border-bottom: none; }
    }

    .campaign-info { flex: 1; min-width: 0; }

    .campaign-desc {
      margin: 2px 0 6px;
      font-size: 0.85rem;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .campaign-raised { margin: 4px 0 6px; font-size: 0.9rem; }

    .progress-track {
      height: 8px;
      border-radius: 999px;
      background: #edf0f3;
      overflow: hidden;
      max-width: 320px;
    }

    .progress-fill {
      height: 100%;
      border-radius: 999px;
      background: var(--brand-color, #1e87f0);
    }

    .donate-button {
      flex-shrink: 0;
      background: #1e87f0;
      color: #fff;
      border-radius: 8px;
      padding: 8px 18px;
      font-weight: 600;
      font-size: 0.9rem;
      text-decoration: none;

      &:hover { background: #1a78d6; }
    }
  }

  .summary {
    padding: 10px 12px;
    border-radius: 8px;
    background: #fff8e1;
    border: 1px solid #ffe08a;
    margin-bottom: 12px;
    font-size: 0.95rem;

    &.cleared { background: #d1e7dd; border-color: #a3cfbb; }
  }

  .stages, .history {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;

    td, th { padding: 9px 6px; border-bottom: 1px solid #f1f3f5; text-align: left; }

    .due { display: block; font-size: 0.78rem; }
    .amount { text-align: right; font-weight: 700; white-space: nowrap; }
    .action { text-align: right; width: 100px; }
  }

  .paid-pill {
    background: #d1e7dd; color: #0f5132;
    border-radius: 999px; padding: 3px 12px; font-size: 0.78rem; font-weight: 700;
  }

  .plan-row, .status-line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid #f1f3f5;

    .muted { margin-left: 8px; }
  }

  .status-pill {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;

    &.good { background: #d1e7dd; color: #0f5132; }
    &.bad { background: #f8d7da; color: #842029; }
    &.warn { background: #fff3cd; color: #856404; }
    &.neutral { background: #e9ecef; color: #495057; }
  }

  .voucher { margin-top: 12px; }

  .voucher-form {
    display: flex;
    gap: 8px;
    margin-top: 8px;
    flex-wrap: wrap;

    input {
      padding: 8px 10px;
      border: 1px solid #ced4da;
      border-radius: 8px;
      flex: 1;
      min-width: 140px;
    }
  }

  button {
    padding: 8px 16px;
    border: none;
    border-radius: 8px;
    background: #198754;
    color: #fff;
    font-weight: 600;
    cursor: pointer;

    &:disabled { opacity: 0.6; cursor: default; }
  }

  .link-button {
    background: none;
    color: #0d6efd;
    text-decoration: underline;
    padding: 8px 0;
    font-weight: 500;
  }

  .error-text { color: #dc3545; font-size: 0.9rem; }
}
</style>
